import { Component, OnInit } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { ToastService } from '../toast/toast.service';

/**
 * Aviso permanente no dashboard para quem não ativou a verificação em duas
 * etapas (CU-86akgt7pf).
 *
 * Substitui o popup que abria depois do login. A obrigatoriedade de agência para
 * baixo acabou, então não há mais prazo a anunciar nem motivo para interromper o
 * usuário na entrada — mas continua valendo lembrar, e o dashboard é onde ele
 * passa todo dia.
 *
 * O estado vem do endpoint, e não do usuário em `localStorage`: quem ativa pela
 * tela de perfil e volta para o dashboard precisa ver o aviso sumir na hora. O
 * objeto salvo no login só mudaria no login seguinte.
 *
 * ## "Não exibir mais"
 *
 * Um lembrete permanente sem saída vira ruído: a pessoa aprende a não ler aquela
 * faixa, e no dia em que ela disser outra coisa ninguém vai notar. O segundo
 * botão é essa saída, e ela é da CONTA — `users.two_factor_notice_dismissed_at`,
 * não `localStorage` —, porque quem dispensou não deve ver o aviso de novo ao
 * entrar de outro computador.
 *
 * A confirmação é um popover no próprio aviso, e não um modal: a ação é pequena
 * e reversível pelo suporte; parar a tela inteira para perguntar custaria mais
 * atenção do que ela vale.
 *
 * ## Em simulação o aviso não aparece
 *
 * `GET /two-factor` responde pelo dono do token — o usuário REAL —, não pela
 * conta simulada: o header de simulação não muda o escopo desse endpoint. Quem
 * simula veria o próprio status do lado do cliente, dizendo "ative a verificação
 * em duas etapas" sobre uma conta que não é a que está na tela, e o link levaria
 * ao perfil errado. Sem conseguir falar da conta certa, o aviso se cala.
 */
@Component({
  selector: 'app-two-factor-notice',
  templateUrl: './two-factor-notice.component.html',
  styleUrls: ['./two-factor-notice.component.scss']
})
export class TwoFactorNoticeComponent implements OnInit {

  show = false;
  dismissing = false;

  constructor(
    private authService: AuthService,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    if (this.authService.isSimulating) {
      return;
    }

    this.authService.twoFactorStatus().subscribe({
      next: (status) => {
        // `required` não entra na decisão: o aviso é o mesmo para quem tem que
        // ativar e para quem só deveria. Na prática quase todo mundo aqui é do
        // segundo grupo — Administrador e Customer Success são barrados no
        // login sem o fator —, mas com a válvula `two_factor.enforce`
        // desligada eles entram sem ele, e aí ver o aviso é o certo.
        this.show = !status.enabled && !status.notice_dismissed;
      },
      // Falha na consulta não vira aviso: dizer "seu 2FA não está ativo" para
      // quem ativou seria pior do que não dizer nada.
      error: () => this.show = false
    });
  }

  /**
   * O aviso só some depois que o backend confirma.
   *
   * Esconder na hora e gravar em seguida pareceria mais rápido e mentiria no
   * caso que importa: com a requisição falhando, o aviso voltaria no próximo
   * carregamento e a pessoa acharia que o botão não funciona.
   */
  dismiss(popover: NgbPopover): void {
    popover.close();
    this.dismissing = true;

    this.authService.dismissTwoFactorNotice().subscribe({
      next: () => {
        this.dismissing = false;
        this.show = false;
      },
      error: () => {
        this.dismissing = false;
        this.toast.error('Não foi possível ocultar o aviso. Tente de novo.');
      }
    });
  }

}
